import clsx from "clsx";
import { AnimationProps, motion } from "framer-motion";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { FC } from "react";

import { BiesseLogo } from "../../biesse-logo";
import { Cta, CtaProps } from "../../buttons/cta";
import { Container } from "../../container";
import { Highlights } from "../../highlights";
import { RichText } from "../../rich-text";
import { colorScheme } from "./styles";
import { EditorialTextBaseColors, EditorialTextBodySizes, EditorialTextTitleSizes } from "./types";

export interface EditorialTextProps extends Queries.EditorialTextDataFragment {
  isHero?: boolean;
}

const transition: AnimationProps["transition"] = {
  ease: "easeOut",
  duration: 0.6,
};

export const EditorialText: FC<EditorialTextProps> = ({
  title,
  body,
  variant = EditorialTextBaseColors.LIGHT,
  titleSize = EditorialTextTitleSizes.MD,
  bodySize = EditorialTextBodySizes.MD,
  titleAlignment,
  bodyAlignment,
  animated,
  image,
  isHero,
  highlights,
  biesseLogo,
  ctas,
}) => {
  const Title = isHero ? motion.h1 : motion.h2;
  const bgClasses = variant && (colorScheme as Record<string, string>)[variant];

  return (
    <div className={clsx("relative w-full", bgClasses)}>
      {image?.gatsbyImageData && (
        <GatsbyImage
          image={image.gatsbyImageData}
          alt={title ?? ""}
          objectFit="cover"
          className="!w-full !h-80 lg:!h-128 3xl:!h-192"
        />
      )}
      <Container pagePaddingX>
        <div
          className={clsx([
            "gap-5 lg:gap-10 flex flex-col",
            image ? "pt-10 pb-16 lg:pt-16 lg:pb-28" : "py-20 lg:py-28",
          ])}
        >
          {biesseLogo && (
            <motion.div
              {...(animated
                ? {
                    initial: { opacity: 0, y: "50%" },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true, amount: "some" },
                  }
                : {})}
              className={clsx(titleAlignment === "left" ? "self-start" : "self-center")}
            >
              <BiesseLogo
                monochrome
                color={variant === EditorialTextBaseColors.PRIMARY ? "white" : "black"}
              />
            </motion.div>
          )}
          {title && (
            <Title
              {...(animated
                ? {
                    initial: { opacity: 0, y: "100%" },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true, amount: "all" },
                  }
                : {})}
              className={clsx([
                "mb-6",
                titleAlignment === "left" ? "text-left" : "text-center",
                variant === EditorialTextBaseColors.LIGHT ||
                variant === EditorialTextBaseColors.NEUTRAL
                  ? "text-primary"
                  : variant === EditorialTextBaseColors.PRIMARY
                  ? "text-light"
                  : "text-dark",
                titleSize === EditorialTextTitleSizes.LG && "text-5xl lg:text-7xl 3xl:text-8xl",
                titleSize === EditorialTextTitleSizes.MD && "text-4xl lg:text-6xl 3xl:text-7xl",
                titleSize === EditorialTextTitleSizes.SM && "text-3xl lg:text-4xl 3xl:text-5xl",
                titleSize === EditorialTextTitleSizes.XS && "text-2xl lg:text-3xl 3xl:text-4xl",
              ])}
              transition={transition}
            >
              {title}
            </Title>
          )}
          {body?.raw && (
            <motion.div
              {...(animated
                ? {
                    initial: { opacity: 0, y: "50%" },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true, amount: "some" },
                  }
                : {})}
              className={clsx([
                "!leading-snug",
                bodyAlignment === "left" ? "text-left" : "text-center",
                variant === EditorialTextBaseColors.PRIMARY ? "text-light" : "text-dark",
                bodySize === EditorialTextBodySizes.MD &&
                  "text-xl lg:text-3xl xl:text-4xl 3xl:text-5xl font-normal",
                bodySize === EditorialTextBodySizes.SM &&
                  "text-base lg:text-lg xl:text-xl 3xl:text-2xl font-light",
              ])}
              transition={{ ...transition, delay: 0.2 }}
            >
              <RichText raw={body.raw} className="w-full article" />
            </motion.div>
          )}
          {!!ctas?.length && (
            <div className="flex items-center gap-8 justify-center">
              {ctas.map((cta, i) => {
                if (!cta) return null;
                let ctaVariants: [CtaProps["variant"], CtaProps["variant"]] = [
                  "neutral",
                  "neutral-outline",
                ];
                switch (variant) {
                  case EditorialTextBaseColors.PRIMARY:
                    ctaVariants = ["light", "light-outline"];
                    break;
                  case EditorialTextBaseColors.NEUTRAL:
                  case EditorialTextBaseColors.LIGHT:
                    ctaVariants = ["primary", "primary-outline"];
                    break;
                }
                return <Cta variant={ctaVariants[i]} {...cta} key={i} />;
              })}
            </div>
          )}
        </div>
      </Container>
      {highlights && (
        <div className={clsx("w-full px-16 pb-8 overflow-hidden", bgClasses)}>
          <Highlights {...highlights} />
        </div>
      )}
    </div>
  );
};

export const query = graphql`
  fragment EditorialTextData on ContentfulEditorialText {
    __typename
    variant
    title
    titleAlignment
    titleSize
    body {
      raw
    }
    biesseLogo
    bodyAlignment
    bodySize
    animated
    image {
      gatsbyImageData(layout: FIXED, width: 3840, quality: 100)
    }
    highlights {
      ...HighlightsData
    }
    ctas {
      ...CtaData
    }
  }
`;
